import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'sonner';
import type { ReactNode } from 'react';
import { AuthProvider } from '@/auth/AuthContext';
import { ApiError } from '@/api/errors';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      // Polling that keeps running on a hidden tab burns the free-tier backend for
      // nobody's benefit; every interval in this app pauses when the tab is not visible.
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Retrying a 401, 403 or 404 just repeats the same answer. Retrying a
        // validation error repeats a bad request.
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

export function Providers({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          {children}
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
